import { useEffect, useMemo, useState } from "react";
import { Plus, Home, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ViewToggle, useViewMode } from "@/components/ViewToggle";
import { PortfolioForm } from "@/components/portfolio/PortfolioForm";
import { PortfolioPresentation } from "@/components/portfolio/PortfolioPresentation";
import { PortfolioFilters, applyFilters, EMPTY_FILTERS } from "@/components/portfolio/PortfolioFilters";
import { PortfolioList, PortfolioGrid } from "@/components/portfolio/PortfolioViews";
import { Pager, SearchBox, usePaged } from "@/components/Pager";
import { toast } from "sonner";

const PAGE = 30;

export default function PortfoliosPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("active");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [mode, setMode] = useViewMode("portfolios");
  const [form, setForm] = useState(() => (new URLSearchParams(window.location.search).get("new") ? { editing: null } : null));
  const [viewer, setViewer] = useState(null);

  const load = async () => setItems((await api.get("/portfolios")).data);
  useEffect(() => { load(); }, []);

  const active = items.filter((p) => !p.archived);
  const archived = items.filter((p) => p.archived);
  const cities = useMemo(() => [...new Set(items.map((p) => p.city).filter(Boolean))].sort(), [items]);
  const shown = useMemo(() => applyFilters(tab === "active" ? active : archived, filters), [items, tab, filters]);
  const [limit, setLimit] = useState(PAGE);
  const [pq, setPq] = useState("");
  const paged = usePaged(shown, pq);
  useEffect(() => setLimit(PAGE), [tab, filters]);
  const canManage = (p) => p && (p.owner_id === user.id || user.role === "BROKER");

  const setStatus = async (status) => {
    try {
      await api.put(`/portfolios/${viewer.id}/status`, { status });
      toast.success(status === "ACTIVE" ? "Portföy tekrar yayına alındı" : "Portföy arşive taşındı");
      setViewer(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/portfolios/${viewer.id}`);
    toast.success("Silindi");
    setViewer(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Portföy</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>İlanlarım</h1>
          <p className="text-slate-500 mt-1">Ofisinize ait portföyler. Satılan veya kiralanan ilanlar otomatik olarak arşive taşınır.</p>
        </div>
        <Button data-testid="add-portfolio-btn" onClick={() => setForm({ editing: null })} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Yeni Portföy</Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger data-testid="tab-active-portfolios" value="active"><Home className="w-3.5 h-3.5 mr-1.5" />Yayında ({active.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-archive-portfolios" value="archive"><Archive className="w-3.5 h-3.5 mr-1.5" />Arşiv ({archived.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-3">
          <span data-testid="portfolio-result-count" className="text-sm text-slate-500">{paged.total} sonuç</span>
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <SearchBox value={pq} onChange={setPq} placeholder="Başlık, ilçe, mahalle, fiyat ara..." testid="portfolio-search-input" />
      </div>
      <PortfolioFilters filters={filters} onChange={setFilters} cities={cities} />

      {shown.length === 0 ? (
        <Card data-testid="portfolio-empty" className="p-16 text-center text-slate-500 border-dashed">
          <Home className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          {(tab === "active" ? active : archived).length === 0 ? (tab === "active" ? "Henüz yayında portföy yok." : "Arşivde portföy yok.") : "Filtrelere uyan portföy bulunamadı."}
        </Card>
      ) : mode === "list" ? <PortfolioList items={paged.items} onOpen={setViewer} /> : <PortfolioGrid items={paged.items} onOpen={setViewer} />}
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} testid="portfolios-pager" />

      <PortfolioForm open={!!form} editing={form?.editing} onClose={() => setForm(null)} onSaved={load} />
      <PortfolioPresentation
        item={viewer}
        canManage={canManage(viewer)}
        onClose={() => setViewer(null)}
        onEdit={() => { setForm({ editing: viewer }); setViewer(null); }}
        onDelete={remove}
        onStatus={setStatus}
      />
    </div>
  );
}
